// NPM
import React from 'react';
import get from 'lodash/get';
import template from 'lodash/template';
import camelCase from 'lodash/camelCase';
import PropTypes from 'prop-types';
// Components
import Input from '../gluValidation/Input';
import Criteria from '../validation/Criteria';
// Utilities & Services
import { lt, gt, regexp } from '../../utils/validations/gluValidations';
import { UserShape } from '../../api/user/props';
import AppPropTypes from '../../proptypes/AppPropTypes';

const FirstTimeCreateNewPassword = ({ APIData, classes, errors, locale, user, values }) => {
  const securityValues = values.security || {};
  // Used for Criteria component flags
  const passesRegex = new RegExp(get(APIData, 'passwordRequirements.passwordPattern', '.*')).test(
    securityValues.password
  );
  const passesMinLengthValidation =
    securityValues.password &&
    securityValues.password.length >= get(APIData, 'passwordRequirements.minPasswordLength');
  const passesMaxLengthValidation =
    securityValues.password &&
    securityValues.password.length <= get(APIData, 'passwordRequirements.maxUserPasswordLength');
  const passwordExclusionProperties = get(APIData, 'passwordRequirements.passwordExclusions', []);
  let passesExcludedValuesValidation = true;
  for (let index = 0; index < passwordExclusionProperties.length; index += 1) {
    if (
      securityValues.password &&
      securityValues.password.includes(
        get(APIData, `passwordRequirements.${passwordExclusionProperties[index]}`)
      )
    ) {
      passesExcludedValuesValidation = false;
    }
  }
  const passesSamePasswordValidation = securityValues.password !== get(user, 'tempPassword');

  // Using properties from API get words that cannot be used
  const passwordExclusions = passwordExclusionProperties.map((key) =>
    get(APIData, `passwordRequirements.${key}`)
  );

  const passesRetypePasswordValidation = securityValues.password === securityValues.retypePassword;

  // When password changes and retype password is not disabled, update whether the two match or not
  React.useEffect(() => {
    if (securityValues.password && get(errors, 'password') === undefined) {
      const el = document.querySelector('input[name="retypePassword"]');
      // Force input to revalidate
      if (el) {
        const originalElementFocus = document.activeElement;
        el.focus();
        el.blur();
        originalElementFocus.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityValues.password]);

  return (
    <div className={classes.wrapper}>
      <h2>
        <b>{locale['security.createNewPassword']}</b>
      </h2>
      <Input
        name="password"
        type="password"
        className={`${classes.fullWidth} form-group`}
        data-qa="password"
        label={locale['common.password']}
        validators={[
          { required: true, msg: locale['validation.required'] },
          gt({
            minLength: get(APIData, 'passwordRequirements.minPasswordLength'),
            msg: template(locale['validation.minNCharacters'])({
              min: get(APIData, 'passwordRequirements.minPasswordLength')
            })
          }),
          lt({
            maxLength: get(APIData, 'passwordRequirements.maxUserPasswordLength'),
            msg: template(locale['validation.maxNCharacters'])({
              max: get(APIData, 'passwordRequirements.maxUserPasswordLength')
            })
          }),
          // Check for special characters according to API regex
          regexp({
            regexp: new RegExp(get(APIData, 'passwordRequirements.passwordPattern', '.*')),
            msg: get(
              APIData,
              'passwordRequirements.passwordPatternDescription',
              locale['validation.regexp']
            )
          }),
          // Check that the user is entering a different password
          {
            fn: (name, _, data) => {
              if (data[name] === get(user, 'tempPassword')) return false;
              return true;
            },
            msg: 'New Password must be different than the Current Password'
          },
          // Check against passwordExclusions to error out any use of personal info
          {
            fn: (name, _, data) => {
              let containsExcludedValue = false;
              for (let index = 0; index < passwordExclusions.length; index += 1) {
                if (data[name].includes(passwordExclusions[index])) containsExcludedValue = true;
              }
              if (containsExcludedValue) {
                return `Password cannot contain ${get(
                  APIData,
                  'passwordRequirements.passwordExclusions',
                  []
                )
                  .map((key) => locale[`password.${camelCase(key)}`])
                  .filter(Boolean)
                  .join(', ')} or any personal information`;
              }
              return true;
            }
          }
        ]}
      />
      <br />
      <Input
        id="retypePassword"
        name="retypePassword"
        type="password"
        className={`${classes.fullWidth} form-group`}
        data-qa="retypePassword"
        // If there is no password entered yet or if that password doesn't pass requirements then disable
        disabled={!securityValues.password || get(errors, 'password') !== undefined}
        label="Retype Password"
        validators={[
          { required: true, msg: locale['validation.required'] },
          // Make sure passwords match
          { fn: (name, _, data) => data[name] === data.password, msg: 'Passwords must match!' }
        ]}
      />
      <br />
      {/* This stuff is for display only */}
      <div className="form-group">
        <ul className={classes.validationChecks}>
          <li>
            <Criteria passing={passesRegex}>
              {get(APIData, 'passwordRequirements.passwordPatternDescription')}
            </Criteria>
          </li>
          <li>
            <Criteria passing={passesSamePasswordValidation}>
              New Password must be different than the Current Password
            </Criteria>
          </li>
          <li>
            <Criteria passing={passesExcludedValuesValidation}>
              {/* Generates string with localized excluded properties from API */}
              {`Password cannot contain ${get(
                APIData,
                'passwordRequirements.passwordExclusions',
                []
              )
                .map((key) => locale[`password.${camelCase(key)}`])
                .filter(Boolean)
                .join(', ')} ${
                get(APIData, 'passwordRequirements.passwordExclusions', []).length > 1 && ' or'
              } any personal information`}
            </Criteria>
          </li>
          <li>
            <Criteria passing={passesMinLengthValidation && passesMaxLengthValidation}>
              Length must be between {get(APIData, 'passwordRequirements.minPasswordLength')} and{' '}
              {get(APIData, 'passwordRequirements.maxUserPasswordLength')}
            </Criteria>
          </li>
          <li>
            <Criteria passing={passesRetypePasswordValidation}>
              {locale['password.match']}
            </Criteria>
          </li>
        </ul>
      </div>
    </div>
  );
};

FirstTimeCreateNewPassword.propTypes = {
  APIData: PropTypes.shape({}).isRequired,
  classes: AppPropTypes.jssClasses.isRequired,
  locale: AppPropTypes.locale.isRequired,
  errors: PropTypes.PropTypes.shape({}).isRequired,
  user: UserShape.isRequired,
  values: PropTypes.shape({}).isRequired
};

export default FirstTimeCreateNewPassword;
